/* Elmsett F-97 (Owner 2026-10-08, owner-calls.md "2026-10-08 F-97"): a product photograph opens whole on screen and
   zooms only as far as its file stays sharp. Contract: 08-ai-systems/governance/acceptance-contracts/F-97-photo-zoom-fit.md.
   A tap or click on a photograph in the product gallery opens PhotoSwipe 5.4.4 (vendored, MIT) at fit instead of Dawn's
   product-modal. Fit: the whole photograph inside the screen, as large as the screen allows. Cap: the file's width in
   pixels divided by 1.5 x its fitted CSS width, at most 3 on a touch phone and 2 on a computer; under 1.1 it does not zoom.
   Double-tap (phone) or click (computer) zooms to the lesser of 2 and the cap; pinch reaches the cap. At fit the image
   drawn is at least the fitted width x the device pixel ratio (Shopify's width= parameter), the original once zoomed.
   PhotoSwipe and its stylesheets load on the first tap, not with the page. Video and 3D media keep Dawn's modal. */
(() => {
  if (window.ElmsettPhotoViewer) return;
  const SELECTOR = '.product__modal-opener--image .product__media-toggle[data-media-id]';
  const STEP = 100;
  const configs = new Map();
  let lib = null;
  let busy = false;

  const config = (sectionId) => {
    if (!configs.has(sectionId)) {
      let value = null;
      const el = document.querySelector('script[data-es-photo-viewer="' + CSS.escape(sectionId) + '"]');
      try { value = el ? JSON.parse(el.textContent) : null; } catch (e) { value = null; }
      configs.set(sectionId, value && Array.isArray(value.photos) && value.photos.length ? value : null);
    }
    return configs.get(sectionId);
  };
  const sectionOf = (opener) => (opener.getAttribute('data-modal') || '').replace(/^#ProductModal-/, '');
  const touchPhone = () => window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const dpr = () => window.devicePixelRatio || 1;

  // The rule, in PhotoSwipe's terms: zoom level 1 draws one file pixel per CSS pixel, so the fitted CSS width is
  // width x fit and the cap (times fit) is width / (1.5 x width x fit) = 1 / (1.5 x fit).
  const fitOf = (zl) => Math.min(zl.panAreaSize.x / zl.elementSize.x, zl.panAreaSize.y / zl.elementSize.y);
  const capOf = (zl, limit) => Math.min(limit, 1 / (1.5 * fitOf(zl)));
  const zooms = (zl, limit) => !zl || !zl.panAreaSize || !zl.elementSize || capOf(zl, limit) >= 1.1;

  const withWidth = (src, width) => {
    const url = new URL(src, location.href);
    url.searchParams.set('width', String(width));
    return url.href;
  };
  // The narrowest file at least as wide as the photograph is drawn at fit on this screen, in steps of 100 px; the
  // original when that would be as wide as the original.
  const fitFile = (photo, drawnCss) => {
    const need = Math.ceil(drawnCss * dpr() - 0.01);
    const width = Math.ceil(need / STEP) * STEP;
    return width >= photo.w ? { src: photo.src, width: photo.w } : { src: withWidth(photo.src, width), width };
  };
  const viewportFitCss = (photo) => {
    const vw = document.documentElement.clientWidth, vh = window.innerHeight;
    return Math.min(vw / photo.w, vh / photo.h) * photo.w;
  };

  const css = (href) => new Promise((resolve, reject) => {
    if (document.querySelector('link[data-es-photo-viewer-css="' + href + '"]')) return resolve();
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.setAttribute('data-es-photo-viewer-css', href);
    link.onload = () => resolve();
    link.onerror = () => reject(new Error('stylesheet ' + href));
    document.head.appendChild(link);
  });
  const load = (c) => {
    if (!lib) {
      lib = Promise.all([
        import(new URL(c.lib, location.href).href),
        css(new URL(c.css, location.href).href),
        css(new URL(c.theme, location.href).href),
      ]).then(([module]) => module.default).catch((error) => { lib = null; throw error; });
    }
    return lib;
  };

  // Photographs in the order the gallery shows them, each once.
  const photosFor = (opener, c) => {
    const byId = new Map(c.photos.map((p) => [String(p.id), p]));
    const scope = opener.closest('media-gallery') || opener.closest('.product') || document;
    const seen = new Set(), list = [];
    scope.querySelectorAll(SELECTOR).forEach((toggle) => {
      const id = toggle.getAttribute('data-media-id');
      if (seen.has(id) || !byId.has(id)) return;
      seen.add(id);
      list.push({ photo: byId.get(id), toggle });
    });
    return list;
  };

  const sharpen = (pswp, slide) => {
    if (!slide || !slide.content || !slide.data || !slide.data.esPhoto) return;
    const img = slide.content.element;
    if (!img || img.tagName !== 'IMG') return;
    const photo = slide.data.esPhoto;
    const zoomed = slide.currZoomLevel > slide.zoomLevels.initial * 1.001;
    const want = zoomed ? { src: photo.src, width: photo.w } : fitFile(photo, slide.width * slide.currZoomLevel);
    const have = Number(img.getAttribute('data-es-width')) || 0;
    if (want.width <= have) return;
    img.setAttribute('data-es-width', String(want.width));
    img.src = want.src;
    slide.data.src = want.src;
    slide.data.esWidth = want.width;
  };

  // F-113a: a customer photo is not in Dawn's modal, so when PhotoSwipe cannot load, its tap leaves the page as it is.
  const fallback = (opener, toggle) => {
    if (/^es-cp-/.test(toggle.getAttribute('data-media-id') || '')) return;
    const modal = document.querySelector(opener.getAttribute('data-modal'));
    if (modal && typeof modal.show === 'function') modal.show(toggle);
  };

  async function open(opener, toggle) {
    const c = config(sectionOf(opener));
    if (!c || busy) return false;
    const list = photosFor(opener, c);
    const index = list.findIndex((item) => item.toggle === toggle || String(item.photo.id) === toggle.getAttribute('data-media-id'));
    if (index < 0) return false;
    return show(c, list, index, toggle, () => fallback(opener, toggle));
  }

  // F-113a: the viewer for both ways in, a photograph of the gallery (the gallery's photographs, in its order) and a
  // tile of the "Customer photos" row (the customer photos only). list: [{ photo, toggle }]; focus returns to toggle.
  async function show(c, list, index, toggle, onFail) {
    if (busy) return false;
    busy = true;
    let PhotoSwipe;
    try {
      PhotoSwipe = await load(c);
    } catch (error) {
      busy = false;
      if (onFail) onFail();
      return true;
    }
    const limit = touchPhone() ? 3 : 2;
    const reduce = reducedMotion();
    const dataSource = list.map(({ photo, toggle: t }) => {
      const file = fitFile(photo, viewportFitCss(photo));
      // A row tile is a square crop, so only a gallery photograph lends its picture while the file loads (F-113a).
      const shownIn = t.closest('modal-opener');
      const shown = shownIn ? shownIn.querySelector('.product__media img') : null;
      const item = { src: file.src, width: photo.w, height: photo.h, alt: photo.alt || '', esPhoto: photo, esWidth: file.width };
      if (shown && shown.complete && shown.naturalWidth && shown.currentSrc) item.msrc = shown.currentSrc;
      return item;
    });
    const pswp = new PhotoSwipe({
      dataSource,
      index,
      mainClass: 'es-pswp',
      bgOpacity: 1,
      padding: { top: 0, bottom: 0, left: 0, right: 0 },
      initialZoomLevel: (zl) => fitOf(zl),
      secondaryZoomLevel: (zl) => (zooms(zl, limit) ? Math.min(2, capOf(zl, limit)) * fitOf(zl) : fitOf(zl)),
      maxZoomLevel: (zl) => (zooms(zl, limit) ? capOf(zl, limit) * fitOf(zl) : fitOf(zl)),
      imageClickAction: 'zoom',
      doubleTapAction: 'zoom',
      tapAction: false,
      bgClickAction: 'close',
      zoom: false,
      returnFocus: false,
      trapFocus: true,
      preload: [1, 1],
      showHideAnimationType: reduce ? 'none' : 'fade',
      showAnimationDuration: reduce ? 0 : 200,
      hideAnimationDuration: reduce ? 0 : 200,
      zoomAnimationDuration: reduce ? 0 : 250,
      // No words of the viewer's own: a photograph that fails to load leaves an empty cream slide, not PhotoSwipe's English.
      errorMsg: '',
      closeTitle: c.labels.close,
      arrowPrevTitle: c.labels.previous,
      arrowNextTitle: c.labels.next,
    });
    pswp.addFilter('isContentZoomable', (zoomable, content) => zoomable && zooms(content.slide && content.slide.zoomLevels, limit));
    pswp.on('contentAppend', ({ content }) => {
      if (content.element && content.element.tagName === 'IMG' && content.data.esWidth) {
        content.element.setAttribute('data-es-width', String(content.data.esWidth));
      }
    });
    // F-113a: a customer photo keeps its label in the viewer, "Customer photo" over "First L., STATE". Each customer
    // slide carries its own label inside its slide holder, so the label travels with its photograph through a swipe and
    // never sits over a neighbouring photograph (review round 3 B1). It is placed 16px inside the photograph's visible
    // bottom-left corner, as the gallery's label is, never nearer the window's edges than 16px (a zoomed photograph
    // fills the window); its width follows the visible photograph as the gallery's does: 62% of it, at most 220px, never
    // wider than it less 32px; where the photograph is narrower than the label's words need with their insets, it is the
    // camera mark, its words kept for screen readers.
    const captionFor = (slide) => {
      const p = slide && slide.data && slide.data.esPhoto;
      if (!p || !p.label || !slide.holderElement) return null;
      if (!slide.esCaption) {
        const el = document.createElement('div'), icon = document.createElement('span'), kind = document.createElement('span'), by = document.createElement('span');
        el.className = 'pswp__es-caption pswp__hide-on-close';
        icon.className = 'es-pswp-caption__icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.innerHTML = '<svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path d="M5.6 3.2h4.8l1 1.6h2a1 1 0 0 1 1 1v6.4a1 1 0 0 1-1 1H2.6a1 1 0 0 1-1-1V5.8a1 1 0 0 1 1-1h2z" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/><circle cx="8" cy="8.9" r="2.4" fill="none" stroke="currentColor" stroke-width="1.3"/></svg>';
        kind.className = 'es-pswp-caption__kind';
        by.className = 'es-pswp-caption__by';
        kind.textContent = p.label;
        by.textContent = p.first + (p.initial ? '\u00a0' + p.initial + '.' : '') + ', ' + p.state;
        el.append(icon, kind, document.createTextNode(' '), by);
        slide.esCaption = el;
      }
      if (slide.esCaption.parentNode !== slide.holderElement) slide.holderElement.appendChild(slide.esCaption);
      return slide.esCaption;
    };
    // The width its words need on one line each, plus the pill's 32px of padding (measured once, before any squeeze).
    const wordsNeed = (el) => {
      if (!el.esNeed) {
        let widest = 0;
        el.querySelectorAll('.es-pswp-caption__kind,.es-pswp-caption__by').forEach((span) => {
          const r = document.createRange();
          r.selectNodeContents(span);
          widest = Math.max(widest, r.getBoundingClientRect().width);
        });
        if (widest) el.esNeed = Math.ceil(widest) + 32;
      }
      return el.esNeed || 170;
    };
    // Shown exactly while its photograph is drawn in the slide (review round 4 B1: never alone on the cream ground while
    // PhotoSwipe fades in or fetches the file; round 5 B1: never missing from a photograph already drawn). The
    // photograph is drawn when the loaded picture is in the slide, or when PhotoSwipe shows the gallery's own picture of
    // the same photograph as its placeholder. Once shown on a picture it stays while that picture stays in the slide, so
    // F-97's swap to a sharper file on zoom never blinks it.
    const painted = (img) => Boolean(img && img.tagName === 'IMG' && img.isConnected && img.naturalWidth > 0 && getComputedStyle(img).display !== 'none');
    const drawn = (slide) => {
      const c = slide.content, img = c && c.element;
      if (img && img === slide.esShownOn && img.isConnected) return true;
      if (c && c.state === 'loaded' && painted(img)) { slide.esShownOn = img; return true; }
      return painted(c && c.placeholder && c.placeholder.element);
    };
    // The picture reaches a slide that is not the current one after its decode, with no event of PhotoSwipe's to follow;
    // watching the slide holder for the picture's arrival shows the caption in the same frame.
    const watch = (slide) => {
      if (slide.esWatch || !slide.holderElement) return;
      slide.esWatch = new MutationObserver(() => placeCaption(slide));
      slide.esWatch.observe(slide.holderElement, { childList: true, subtree: true });
    };
    const placeCaption = (slide) => {
      const el = captionFor(slide);
      if (!el) return;
      watch(slide);
      el.style.visibility = drawn(slide) ? '' : 'hidden';
      if (!slide.pan || !slide.currZoomLevel || !pswp.viewportSize) return;
      const w = slide.width * slide.currZoomLevel, h = slide.height * slide.currZoomLevel;
      const left = Math.max(0, slide.pan.x), right = Math.min(pswp.viewportSize.x, slide.pan.x + w);
      const seen = Math.max(0, right - left);
      el.classList.toggle('es-pswp-caption--mark', seen < wordsNeed(el) + 32);
      el.style.minWidth = Math.min(220, Math.floor(seen * 0.62)) + 'px';
      el.style.maxWidth = Math.max(0, Math.floor(seen - 32)) + 'px';
      el.style.left = Math.round(left) + 16 + 'px';
      el.style.bottom = Math.max(0, Math.round(pswp.viewportSize.y - (slide.pan.y + h))) + 16 + 'px';
    };
    // Checked again once the picture can have reached the slide: PhotoSwipe appends it after its own events fire.
    const placeSoon = (slide) => { if (slide) requestAnimationFrame(() => placeCaption(slide)); };
    pswp.on('afterSetContent', ({ slide }) => placeCaption(slide));
    pswp.on('zoomPanUpdate', ({ slide }) => placeCaption(slide));
    pswp.on('change', () => placeCaption(pswp.currSlide));
    pswp.on('loadComplete', ({ slide }) => placeSoon(slide));
    pswp.on('loadError', ({ slide }) => placeSoon(slide));
    pswp.on('contentAppend', ({ content }) => placeSoon(content && content.slide));
    pswp.on('appendHeavyContent', ({ slide }) => placeSoon(slide));
    pswp.on('contentAppendImage', ({ content }) => placeSoon(content && content.slide));
    pswp.on('resize', () => requestAnimationFrame(() => pswp.mainScroll.itemHolders.forEach((holder) => { if (holder.slide) placeCaption(holder.slide); })));
    pswp.on('slideDestroy', ({ slide }) => {
      if (slide.esWatch) slide.esWatch.disconnect();
      if (slide.esCaption) slide.esCaption.remove();
    });
    const check = () => sharpen(pswp, pswp.currSlide);
    pswp.on('zoomPanUpdate', ({ slide }) => { if (slide === pswp.currSlide) check(); });
    pswp.on('change', check);
    pswp.on('resize', check);
    pswp.on('destroy', () => {
      busy = false;
      window.ElmsettPhotoViewer.current = null;
      document.documentElement.classList.remove('es-pswp-open');
      if (toggle.isConnected) toggle.focus({ preventScroll: true });
    });
    // PhotoSwipe's trapFocus pulls focus back only once it lands on something outside; Tab past the last control hands
    // focus to the browser itself, so Tab and Shift+Tab wrap among the viewer's own visible controls instead.
    const wrapTab = (event) => {
      if (event.key !== 'Tab' || !pswp.element) return;
      const stops = Array.from(pswp.element.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])'))
        .filter((el) => !el.disabled && el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
      if (!stops.length) { event.preventDefault(); pswp.element.focus(); return; }
      const at = stops.indexOf(document.activeElement);
      const last = stops.length - 1;
      if (event.shiftKey ? at <= 0 : at === last || at < 0) {
        event.preventDefault();
        stops[event.shiftKey ? last : 0].focus();
      }
    };
    document.addEventListener('keydown', wrapTab, true);
    pswp.on('destroy', () => document.removeEventListener('keydown', wrapTab, true));
    // Lock the page behind the viewer before PhotoSwipe measures the screen.
    document.documentElement.classList.add('es-pswp-open');
    pswp.init();
    if (pswp.element) {
      pswp.element.setAttribute('aria-modal', 'true');
      pswp.element.setAttribute('aria-label', c.labels.dialog);
    }
    window.ElmsettPhotoViewer.current = pswp;
    return true;
  }

  document.addEventListener('click', (event) => {
    const toggle = event.target && event.target.closest ? event.target.closest(SELECTOR) : null;
    if (!toggle) return;
    const opener = toggle.closest('modal-opener');
    if (!opener || !config(sectionOf(opener))) return;
    event.preventDefault();
    event.stopPropagation();
    open(opener, toggle);
  }, true);

  // Start fetching the viewer the moment a finger or pointer lands on a photograph, so the tap's click finds it ready.
  document.addEventListener('pointerdown', (event) => {
    const toggle = event.target && event.target.closest ? event.target.closest(SELECTOR) : null;
    const opener = toggle && toggle.closest('modal-opener');
    const c = opener && config(sectionOf(opener));
    if (c) load(c).catch(() => {});
  }, { capture: true, passive: true });

  // F-113a: a tile of the "Customer photos" row opens the viewer on the customer photos only, at that photo; "+N" opens
  // at the first photo the row does not show. With no viewer (the lightbox setting off, or PhotoSwipe failing to load)
  // a tile goes to the "In customers' homes" block instead.
  const ROW = '[data-es-cp-row] [data-es-cp-open]';
  const rowConfig = (tile) => config(tile.closest('[data-es-cp-row]').getAttribute('data-es-cp-row'));
  const toBlock = () => {
    const block = document.querySelector('[data-es-home-photos]');
    if (!block) return;
    // 24px of room above the heading, so its text and focus ring stay clear of the window's edge (round 4 S2).
    window.scrollTo({ top: Math.max(0, block.getBoundingClientRect().top + window.scrollY - 24), behavior: reducedMotion() ? 'auto' : 'smooth' });
    // Keyboard focus goes with the page to the block's heading, so the next Tab continues from there (round 3 S3).
    const heading = block.querySelector('.es-home-photos__heading') || block;
    if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
    heading.focus({ preventScroll: true });
  };
  document.addEventListener('click', (event) => {
    const tile = event.target && event.target.closest ? event.target.closest(ROW) : null;
    if (!tile) return;
    event.preventDefault();
    const c = rowConfig(tile);
    const photos = c ? c.photos.filter((p) => p.label) : [];
    const index = photos.findIndex((p) => String(p.id) === tile.getAttribute('data-media-id'));
    if (index < 0) { toBlock(); return; }
    show(c, photos.map((photo) => ({ photo, toggle: tile })), index, tile, toBlock);
  });
  document.addEventListener('pointerdown', (event) => {
    const tile = event.target && event.target.closest ? event.target.closest(ROW) : null;
    const c = tile && rowConfig(tile);
    if (c) load(c).catch(() => {});
  }, { capture: true, passive: true });

  window.ElmsettPhotoViewer = { current: null, open: (toggle) => open(toggle.closest('modal-opener'), toggle) };
})();
